import { Container, Singleton } from 'typescript-ioc/es5'
import { AppService } from './app'
import aqe from '@pushly/aqe'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { ExternalEventType } from '@pushly/models/lib/structs/external-events/external-event-type'
import { ApiVersion } from '../enums/api-version.enum'

/**
 * Reads a domain's registered external event types.
 *
 * The registry is what a journey's external-event trigger picks from, and what the condition
 * builder types an event property against — the property map is registered per event name and
 * schema version, so nothing about an event's shape is typed from memory anywhere in the UI.
 *
 * Case conversion is skipped so the registered property keys arrive verbatim. A partner names its
 * own properties, and a key the browser rewrote no longer matches the one the router substitutes
 * at send time.
 */
@Singleton
export class ExternalEventTypeService extends AbstractRequestService {
    public appService: AppService

    public constructor() {
        super()
        this.appService = Container.get(AppService)
    }

    public async list(domainId: number, options: IApiCallOptions = {}): Promise<IApiResponse<ExternalEventType[]>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.request(HttpMethod.GET, this.getEndpoint(domainId, options.version), {
                ...options,
                config: { skipCaseConversion: true },
                responseType: [ExternalEventType],
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    private getEndpoint = (domainId: number, version: ApiVersion | undefined) =>
        `${aqe.defaults.publicApiDomain}/${version ?? ApiVersion.V3}domains/${domainId}/event-types`
}
